//frontend/src/components/Home.jsx

import React, { useState } from 'react';
import { Container, Row, Col, Button, Card, Carousel, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
    FaRocket, FaShieldAlt, FaHeadset, FaBriefcase, 
    FaGraduationCap, FaPlane, FaUsers, FaLaptopCode, FaHospital,
    FaHammer, FaUtensils, FaShoppingCart, FaTruck, FaStar,
    FaCheckCircle, FaChartLine, FaGlobe,
} from 'react-icons/fa';
import { IoMdChatbubbles } from 'react-icons/io';
import ChatBot from './ChatBot/ChatBot';
import { toast } from 'react-toastify';
import './Home.css';

const Home = () => {
    const [showChatBot, setShowChatBot] = useState(false);

    const handleChatBotOpen = () => {
        setShowChatBot(true);
        toast.info('💬 Assistant IA à votre service !');
    };

    const services = [
        {
            icon: <FaBriefcase size={50} />,
            title: 'Visa de Travail',
            description: 'Accédez facilement aux opportunités d\'emploi au Canada avec notre accompagnement personnalisé.',
            badge: 'Le plus demandé',
            color: '#3498db'
        },
        {
            icon: <FaGraduationCap size={50} />,
            title: 'Visa d\'Études',
            description: 'Poursuivez vos études dans les meilleures universités canadiennes.',
            badge: 'Populaire',
            color: '#9b59b6'
        },
        {
            icon: <FaPlane size={50} />,
            title: 'Visa Touristique',
            description: 'Découvrez les merveilles du Canada en toute simplicité.',
            badge: null,
            color: '#e74c3c'
        },
        {
            icon: <FaUsers size={50} />,
            title: 'Regroupement Familial',
            description: 'Rejoignez vos proches déjà installés au Canada.',
            badge: null,
            color: '#27ae60'
        }
    ];

    const jobSectors = [
        { name: 'Technologies de l\'information', demand: '95%', icon: <FaLaptopCode size={40} />, color: '#3498db' },
        { name: 'Santé et services sociaux', demand: '92%', icon: <FaHospital size={40} />, color: '#e74c3c' },
        { name: 'Construction', demand: '88%', icon: <FaHammer size={40} />, color: '#f39c12' },
        { name: 'Hôtellerie et restauration', demand: '85%', icon: <FaUtensils size={40} />, color: '#9b59b6' },
        { name: 'Commerce de détail', demand: '80%', icon: <FaShoppingCart size={40} />, color: '#1abc9c' },
        { name: 'Transport et logistique', demand: '78%', icon: <FaTruck size={40} />, color: '#34495e' }
    ];

    const benefits = [
        {
            icon: <FaChartLine size={50} />,
            title: 'Taux de succès élevé',
            description: '92% de nos dossiers sont approuvés',
            color: '#27ae60'
        },
        {
            icon: <FaRocket size={50} />,
            title: 'Traitement rapide',
            description: 'Dossiers traités en 4-6 semaines',
            color: '#3498db'
        },
        {
            icon: <FaShieldAlt size={50} />,
            title: 'Sécurisé et légal',
            description: 'Processus 100% conforme aux normes canadiennes',
            color: '#e74c3c'
        },
        {
            icon: <FaHeadset size={50} />,
            title: 'Support 24/7',
            description: 'Notre équipe est toujours disponible',
            color: '#f39c12'
        }
    ];

    const testimonials = [
        {
            name: 'Marie Kouadio',
            role: 'Infirmière',
            text: 'Grâce à E-Visa, j\'ai obtenu mon visa de travail en seulement 5 semaines. Le service était impeccable!',
            rating: 5,
            image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=150&h=150&fit=crop&crop=face'
        },
        {
            name: 'Jean-Paul Dembélé',
            role: 'Développeur',
            text: 'L\'accompagnement était excellent. Aujourd\'hui, je travaille à Toronto dans une entreprise de tech.',
            rating: 5,
            image: 'https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=150&h=150&fit=crop&crop=face'
        },
        {
            name: 'Sophie Mensah',
            role: 'Chef cuisinière',
            text: 'Le processus était simple et transparent. Je recommande vivement E-Visa à tous!',
            rating: 5,
            image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face'
        }
    ];

    const stats = [
        { number: '15,000+', label: 'Visas approuvés', icon: <FaCheckCircle /> },
        { number: '92%', label: 'Taux de succès', icon: <FaChartLine /> },
        { number: '40+', label: 'Pays représentés', icon: <FaGlobe /> },
        { number: '24/7', label: 'Support client', icon: <FaHeadset /> }
    ];

    return (
        <div className="home-page">
            {/* Hero Section avec Carousel */}
            <section className="hero-section">
                <Carousel fade interval={5000} className="hero-carousel">
                    <Carousel.Item>
                        <div className="carousel-overlay"></div>
                        <img
                            className="hero-image"
                            src="https://images.unsplash.com/photo-1517935706615-2717063c2225?w=1920&q=80"
                            alt="Toronto skyline"
                        />
                        <Carousel.Caption className="hero-content">
                            <h1 className="hero-title animate-fade-in">
                                Réalisez votre <span className="text-highlight">rêve canadien</span>
                            </h1>
                            <p className="hero-subtitle">
                                Votre passerelle vers une nouvelle vie professionnelle au Canada
                            </p>
                            <div className="hero-buttons">
                                <Button as={Link} to="/register" size="lg" className="btn-primary-solid me-3">
                                    <FaRocket className="me-2" />
                                    <span>Commencer maintenant</span>
                                </Button>
                                <Button 
                                    size="lg" 
                                    className="btn-white-solid"
                                    onClick={handleChatBotOpen}
                                >
                                    <IoMdChatbubbles className="me-2" size={24} />
                                    <span>Parler à notre IA</span>
                                </Button>
                            </div>
                        </Carousel.Caption>
                    </Carousel.Item>

                    <Carousel.Item>
                        <div className="carousel-overlay"></div>
                        <img
                            className="hero-image"
                            src="https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=1920&q=80"
                            alt="Professionals working"
                        />
                        <Carousel.Caption className="hero-content">
                            <h1 className="hero-title animate-fade-in">
                                Plus de <span className="text-highlight">500,000</span> emplois disponibles
                            </h1>
                            <p className="hero-subtitle">
                                Le Canada recherche activement des travailleurs qualifiés comme vous
                            </p>
                            <div className="hero-buttons">
                                <Button as={Link} to="/register" size="lg" className="btn-primary-solid">
                                    <FaBriefcase className="me-2" />
                                    Créer mon dossier
                                </Button>
                            </div>
                        </Carousel.Caption>
                    </Carousel.Item>

                    <Carousel.Item>
                        <div className="carousel-overlay"></div>
                        <img
                            className="hero-image"
                            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80"
                            alt="Success celebration"
                        />
                        <Carousel.Caption className="hero-content">
                            <h1 className="hero-title animate-fade-in">
                                <span className="text-highlight">92% de réussite</span> garantie
                            </h1>
                            <p className="hero-subtitle">
                                Rejoignez les milliers de personnes que nous avons aidées
                            </p>
                            <div className="hero-buttons">
                                <Button as={Link} to="/register" size="lg" className="btn-primary-solid">
                                    <FaCheckCircle className="me-2" />
                                    Je veux mon visa
                                </Button>
                            </div>
                        </Carousel.Caption>
                    </Carousel.Item>
                </Carousel>
            </section>

            {/* Section Statistiques */}
            <section className="stats-section">
                <Container>
                    <Row className="justify-content-center">
                        {stats.map((stat, index) => (
                            <Col md={3} sm={6} key={index} className="mb-4">
                                <div className="stat-card">
                                    <div className="stat-icon">{stat.icon}</div>
                                    <h2 className="stat-number">{stat.number}</h2>
                                    <p className="stat-label">{stat.label}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Section Pourquoi le Canada */}
            <section className="why-canada-section py-5">
                <Container>
                    <div className="section-header text-center mb-5">
                        <h2 className="section-title">Pourquoi choisir le Canada ?</h2>
                        <p className="section-subtitle">
                            Un pays qui offre des opportunités exceptionnelles aux travailleurs étrangers
                        </p>
                    </div>

                    <Row>
                        <Col md={6} className="mb-4">
                            <Card className="benefit-card h-100">
                                <Card.Body>
                                    <div className="benefit-icon-large">
                                        <FaGlobe size={60} color="#3498db" />
                                    </div>
                                    <h4>Environnement accueillant et multiculturel</h4>
                                    <p>
                                        Le Canada est reconnu comme l'un des pays les plus accueillants au monde. 
                                        Avec une politique d'immigration ouverte et une société multiculturelle, 
                                        vous vous sentirez rapidement chez vous.
                                    </p>
                                    <ul className="benefit-list">
                                        <li><FaCheckCircle color="#27ae60" /> Respect de la diversité culturelle</li>
                                        <li><FaCheckCircle color="#27ae60" /> Égalité des chances pour tous</li>
                                        <li><FaCheckCircle color="#27ae60" /> Communautés francophones dynamiques</li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} className="mb-4">
                            <Card className="benefit-card h-100">
                                <Card.Body>
                                    <div className="benefit-icon-large">
                                        <FaBriefcase size={60} color="#e74c3c" />
                                    </div>
                                    <h4>Pénurie de main d'œuvre = Opportunités</h4>
                                    <p>
                                        Le Canada fait face à une importante pénurie de travailleurs qualifiés 
                                        dans plusieurs secteurs. C'est le moment idéal pour saisir votre chance!
                                    </p>
                                    <ul className="benefit-list">
                                        <li><FaCheckCircle color="#27ae60" /> Plus de 500,000 postes à pourvoir</li>
                                        <li><FaCheckCircle color="#27ae60" /> Salaires compétitifs (50,000$ - 100,000$ CAD/an)</li>
                                        <li><FaCheckCircle color="#27ae60" /> Évolution de carrière rapide</li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} className="mb-4">
                            <Card className="benefit-card h-100">
                                <Card.Body>
                                    <div className="benefit-icon-large">
                                        <FaHospital size={60} color="#9b59b6" />
                                    </div>
                                    <h4>Qualité de vie exceptionnelle</h4>
                                    <p>
                                        Le Canada offre l'un des meilleurs systèmes de santé au monde, 
                                        une éducation de qualité et un équilibre travail-vie personnelle idéal.
                                    </p>
                                    <ul className="benefit-list">
                                        <li><FaCheckCircle color="#27ae60" /> Système de santé universel gratuit</li>
                                        <li><FaCheckCircle color="#27ae60" /> Éducation de qualité pour vos enfants</li>
                                        <li><FaCheckCircle color="#27ae60" /> Sécurité et stabilité politique</li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6} className="mb-4">
                            <Card className="benefit-card h-100">
                                <Card.Body>
                                    <div className="benefit-icon-large">
                                        <FaRocket size={60} color="#f39c12" />
                                    </div>
                                    <h4>Processus simplifié et rapide</h4>
                                    <p>
                                        Le gouvernement canadien a simplifié les procédures d'immigration 
                                        pour attirer plus rapidement les talents internationaux.
                                    </p>
                                    <ul className="benefit-list">
                                        <li><FaCheckCircle color="#27ae60" /> Entrée express en 6 mois ou moins</li>
                                        <li><FaCheckCircle color="#27ae60" /> Permis de travail en 4-8 semaines</li>
                                        <li><FaCheckCircle color="#27ae60" /> Voie vers la résidence permanente</li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Section Secteurs qui recrutent */}
            <section className="job-sectors-section py-5 bg-light">
                <Container>
                    <div className="section-header text-center mb-5">
                        <h2 className="section-title">Secteurs qui recrutent activement</h2>
                        <p className="section-subtitle">
                            Des milliers d'opportunités dans ces domaines en forte demande
                        </p>
                    </div>

                    <Row>
                        {jobSectors.map((sector, index) => (
                            <Col md={4} sm={6} key={index} className="mb-4">
                                <Card className="job-sector-card h-100">
                                    <Card.Body className="text-center">
                                        <div className="sector-icon" style={{color: sector.color}}>
                                            {sector.icon}
                                        </div>
                                        <h5 className="sector-name">{sector.name}</h5>
                                        <div className="demand-bar">
                                            <div 
                                                className="demand-fill" 
                                                style={{width: sector.demand, background: sector.color}}
                                            ></div>
                                        </div>
                                        <p className="demand-text">
                                            <strong>{sector.demand}</strong> de demande
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <div className="text-center mt-4">
                        <Button 
                            as={Link} 
                            to="/register" 
                            size="lg" 
                            className="btn-primary-solid"
                        >
                            <FaRocket className="me-2" />
                            Postuler maintenant
                        </Button>
                    </div>
                </Container>
            </section>

            {/* Section Nos Services */}
            <section className="services-section py-5">
                <Container>
                    <div className="section-header text-center mb-5">
                        <h2 className="section-title">Nos services d'accompagnement</h2>
                        <p className="section-subtitle">
                            Un accompagnement complet pour tous vos besoins de visa
                        </p>
                    </div>

                    <Row>
                        {services.map((service, index) => (
                            <Col md={6} lg={3} key={index} className="mb-4">
                                <Card className="service-card h-100">
                                    {service.badge && (
                                        <div className="service-badge">{service.badge}</div>
                                    )}
                                    <Card.Body className="text-center">
                                        <div className="service-icon" style={{color: service.color}}>
                                            {service.icon}
                                        </div>
                                        <h4 className="service-title">{service.title}</h4>
                                        <p className="service-description">{service.description}</p>
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            as={Link}
                                            to="/register"
                                        >
                                            En savoir plus →
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Section Avantages */}
            <section className="benefits-section py-5 bg-light">
                <Container>
                    <div className="section-header text-center mb-5">
                        <h2 className="section-title">Pourquoi choisir E-Visa ?</h2>
                    </div>

                    <Row>
                        {benefits.map((benefit, index) => (
                            <Col md={6} lg={3} key={index} className="mb-4">
                                <div className="benefit-box text-center">
                                    <div className="benefit-icon-large" style={{color: benefit.color}}>
                                        {benefit.icon}
                                    </div>
                                    <h5>{benefit.title}</h5>
                                    <p>{benefit.description}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Section Témoignages */}
            <section className="testimonials-section py-5">
                <Container>
                    <div className="section-header text-center mb-5">
                        <h2 className="section-title">Ce que disent nos clients</h2>
                        <p className="section-subtitle">
                            Des milliers de personnes nous font confiance
                        </p>
                    </div>

                    <Row>
                        {testimonials.map((testimonial, index) => (
                            <Col md={4} key={index} className="mb-4">
                                <Card className="testimonial-card h-100">
                                    <Card.Body>
                                        <div className="testimonial-header">
                                            <img 
                                                src={testimonial.image} 
                                                alt={testimonial.name}
                                                className="testimonial-avatar"
                                            />
                                            <div>
                                                <h5 className="testimonial-name">{testimonial.name}</h5>
                                                <p className="testimonial-role">{testimonial.role}</p>
                                            </div>
                                        </div>
                                        <div className="testimonial-rating mb-2">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <FaStar key={i} color="#f39c12" />
                                            ))}
                                        </div>
                                        <p className="testimonial-text">"{testimonial.text}"</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Section Assistant IA */}
            <section className="ai-assistant-section py-5 bg-light">
                <Container>
                    <Row className="align-items-center">
                        <Col md={6} className="mb-4 mb-md-0">
                            <div className="ai-assistant-image">
                                <img 
                                    src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80" 
                                    alt="AI Assistant"
                                    className="img-fluid rounded"
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="ai-assistant-content">
                                <h2 className="section-title">
                                    <IoMdChatbubbles className="me-3" color="#3498db" />
                                    Assistant IA E-Visa
                                </h2>
                                <p className="lead">
                                    Notre intelligence artificielle est disponible 24/7 pour répondre 
                                    à toutes vos questions sur l'immigration au Canada.
                                </p>
                                <ul className="ai-features-list">
                                    <li><FaCheckCircle color="#27ae60" /> Réponses instantanées à vos questions</li>
                                    <li><FaCheckCircle color="#27ae60" /> Conseils personnalisés selon votre profil</li>
                                    <li><FaCheckCircle color="#27ae60" /> Disponible en français et anglais</li>
                                    <li><FaCheckCircle color="#27ae60" /> Base de connaissances mise à jour</li>
                                </ul>
                                <Button 
                                    size="lg" 
                                    className="btn-primary-solid"
                                    onClick={handleChatBotOpen}
                                >
                                    <IoMdChatbubbles className="me-2" size={24} />
                                    Discuter avec l'IA
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Section CTA Final */}
            <section className="cta-section py-5">
                <Container>
                    <div className="cta-box text-center">
                        <h2 className="cta-title">Prêt à commencer votre nouvelle vie au Canada ?</h2>
                        <p className="cta-text">
                            Rejoignez les milliers de personnes qui ont déjà réalisé leur rêve canadien avec E-Visa
                        </p>
                        <div className="cta-buttons">
                            <Button 
                                as={Link} 
                                to="/register" 
                                size="lg" 
                                className="btn-white-solid me-3"
                            >
                                <FaRocket className="me-2" />
                                Créer mon compte gratuitement
                            </Button>
                            <Button 
                                size="lg" 
                                className="btn-outline-white"
                                onClick={handleChatBotOpen}
                            >
                                <IoMdChatbubbles className="me-2" />
                                Parler à un conseiller
                            </Button>
                        </div>
                        <p className="cta-note mt-3">
                            <FaCheckCircle className="me-2" />
                            Sans engagement • Réponse en 24h • 100% sécurisé
                        </p>
                    </div>
                </Container>
            </section>

            {/* Footer */}
            <footer className="footer-section">
                <Container>
                    <Row className="py-5">
                        <Col md={4} className="mb-4 mb-md-0">
                            <h3 className="footer-brand">
                                🍁 E-Visa
                            </h3>
                            <p className="footer-description">
                                Votre partenaire de confiance pour l'immigration au Canada. 
                                Plus de 15,000 visas approuvés depuis 2020.
                            </p>
                            <div className="social-links">
                                <a href="#" className="social-link"><FaGlobe /></a>
                                <a href="#" className="social-link"><IoMdChatbubbles /></a>
                            </div>
                        </Col>
                        
                        <Col md={2} className="mb-4 mb-md-0">
                            <h5 className="footer-title">Services</h5>
                            <ul className="footer-links">
                                <li><Link to="/visa-travail">Visa de travail</Link></li>
                                <li><Link to="/visa-etudes">Visa d'études</Link></li>
                                <li><Link to="/visa-tourisme">Visa touristique</Link></li>
                                <li><Link to="/regroupement">Regroupement familial</Link></li>
                            </ul>
                        </Col>
                        
                        <Col md={2} className="mb-4 mb-md-0">
                            <h5 className="footer-title">Entreprise</h5>
                            <ul className="footer-links">
                                <li><Link to="/about">À propos</Link></li>
                                <li><Link to="/team">Notre équipe</Link></li>
                                <li><Link to="/contact">Contact</Link></li>
                                <li><Link to="/careers">Carrières</Link></li>
                            </ul>
                        </Col>
                        
                        <Col md={2} className="mb-4 mb-md-0">
                            <h5 className="footer-title">Ressources</h5>
                            <ul className="footer-links">
                                <li><Link to="/blog">Blog</Link></li>
                                <li><Link to="/faq">FAQ</Link></li>
                                <li><Link to="/guides">Guides</Link></li>
                                <li><Link to="/support">Support</Link></li>
                            </ul>
                        </Col>
                        
                        <Col md={2}>
                            <h5 className="footer-title">Légal</h5>
                            <ul className="footer-links">
                                <li><Link to="/privacy">Confidentialité</Link></li>
                                <li><Link to="/terms">Conditions</Link></li>
                                <li><Link to="/cookies">Cookies</Link></li>
                                <li><Link to="/legal">Mentions légales</Link></li>
                            </ul>
                        </Col>
                    </Row>
                    
                    <Row className="border-top pt-4">
                        <Col md={6} className="text-center text-md-start">
                            <p className="footer-copyright mb-0">
                                © 2024 E-Visa. Tous droits réservés.
                            </p>
                        </Col>
                        <Col md={6} className="text-center text-md-end">
                            <p className="footer-made-with mb-0">
                                Fait avec ❤️ pour les futurs Canadiens
                            </p>
                        </Col>
                    </Row>
                </Container>
            </footer>

            {/* ChatBot Modal */}
            <Modal 
                show={showChatBot} 
                onHide={() => setShowChatBot(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <IoMdChatbubbles className="me-2" color="#3498db" />
                        Assistant E-Visa IA
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: 0, height: '500px' }}>
                    <ChatBot />
                </Modal.Body>
            </Modal>

            {/* Floating Chat Button */}
            <button 
                className="floating-chat-button"
                onClick={handleChatBotOpen}
                title="Besoin d'aide ?"
            >
                <IoMdChatbubbles size={28} />
                <span className="chat-text">Besoin d'aide ?</span>
            </button>
        </div>
    );
};

export default Home;